/**
 * opora-edge-spine.js — Operator Pipeline + Thread engine (Phase A)
 * Memory + Context layers. Works with OporaProfile + OporaWire.
 */
(function (global) {
  'use strict';

  var STORAGE_KEY = 'OporaPipeline';
  var MAX_ITEMS = 40;
  var VERSION = 1;

  var TOOL_META = {
    'rfp-decoder':           { type: 'rfp',      label: 'RFP decode' },
    'bid-drafter':           { type: 'bid',      label: 'Bid draft' },
    'walkthrough-voice':     { type: 'walkthrough', label: 'Walkthrough' },
    'scope-of-work-generator': { type: 'sow',    label: 'SOW' },
    'margin-sentinel':       { type: 'margin',   label: 'Margin check' },
    'lost-bid-autopsy':      { type: 'autopsy',  label: 'Lost bid autopsy' },
    'customer-recovery':     { type: 'recovery', label: 'Customer recovery' },
    'review-response':       { type: 'review',   label: 'Review response' },
    'compliance-agent':      { type: 'compliance', label: 'Compliance' },
    'hire-right':            { type: 'hire',     label: 'Hire-Right' },
    'handbook-drafter':      { type: 'handbook', label: 'Handbook' },
    'ask-opora':             { type: 'ask',      label: 'Ask Opora' }
  };

  var PROFILE_FIELDS = ['company_name', 'state', 'vertical', 'company_size', 'city', 'revenue_stage'];

  function uid() {
    return 'op_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
  }

  function esc(s) {
    return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function pipelineUrl() {
    var edge = global.OporaEdge || {};
    var dash = (edge.DASHBOARD_CUSTOM_URL || edge.DASHBOARD_URL || 'https://dashboard.oporasupply.com').replace(/\/$/, '');
    return dash + '/?view=pipeline';
  }

  function loadStore() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      var data = raw ? JSON.parse(raw) : {};
      if (!data.items || !Array.isArray(data.items)) data.items = [];
      if (!data.version) data.version = VERSION;
      return data;
    } catch (e) {
      return { version: VERSION, items: [] };
    }
  }

  function saveStore(data, opts) {
    opts = opts || {};
    try {
      data.items = (data.items || []).slice(0, MAX_ITEMS);
      data.updated_at = new Date().toISOString();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      global.dispatchEvent(new CustomEvent('opora:pipeline-updated', { detail: { items: data.items } }));
      if (!opts.skipSync) syncPipelineToAccount(data.items);
    } catch (e) {}
  }

  function mapItemToDeal(item) {
    var stage = 'prospect';
    if (item.type === 'rfp' && item.status === 'open') stage = 'prospect';
    else if (item.type === 'bid' && item.status === 'pending') stage = 'quoted';
    else if (item.status === 'won') stage = 'won';
    else if (item.status === 'lost') stage = 'lost';
    else if (item.status === 'at_risk') stage = 'quoted';
    return {
      id: item.id,
      name: item.title,
      stage: stage,
      tool: item.tool,
      type: item.type,
      status: item.status,
      margin_checked: !!item.margin_checked,
      output_preview: (item.output_preview || '').slice(0, 500),
      updated_at: item.updated_at
    };
  }

  function syncPipelineToAccount(items) {
    if (!global.OporaEdge || typeof global.OporaEdge.isAuthed !== 'function' || !global.OporaEdge.isAuthed()) return;
    if (typeof global.OporaEdge.fetchApi !== 'function') return;
    // §6: Ask Opora questions must NOT auto-create pipeline deals. We keep the
    // local thread/activity record but exclude chat-style 'ask' items from the
    // pipeline sync so they never land in pipeline_deals. Deals are created for
    // Ask Opora only via the explicit "Send to pipeline" action in the More menu.
    var payload = {
      version: VERSION,
      account_id: (global.OporaAccountFile && global.OporaAccountFile.getId) ? global.OporaAccountFile.getId() : null,
      items: (items || []).filter(function (it) {
        return it && it.tool !== 'ask-opora' && it.type !== 'ask';
      }).map(mapItemToDeal),
      synced_at: new Date().toISOString()
    };
    global.OporaEdge.fetchApi('/api/pipeline/sync', {
      method: 'POST',
      body: JSON.stringify(payload)
    }).catch(function () { /* worker route may not be deployed yet */ });
  }

  function mapDealToItem(deal) {
    return {
      id: deal.id,
      tool: deal.tool || 'bid-drafter',
      type: deal.item_type || deal.type || 'bid',
      title: deal.name || deal.title || 'Pipeline item',
      status: deal.item_status || deal.status || (deal.stage === 'lost' ? 'lost' : deal.stage === 'won' ? 'won' : 'pending'),
      stage: deal.stage || 'prospect',
      created_at: deal.created_at || new Date().toISOString(),
      updated_at: deal.updated_at || new Date().toISOString(),
      margin_checked: !!deal.margin_checked,
      output_preview: deal.output_preview || ''
    };
  }

  function mergeArtifactsIntoPipeline() {
    if (!global.OporaAccountFile || typeof global.OporaAccountFile.listFiles !== 'function') return Promise.resolve(null);
    if (!global.OporaEdge || !global.OporaEdge.isAuthed || !global.OporaEdge.isAuthed()) return Promise.resolve(null);
    return global.OporaAccountFile.listFiles().then(function (files) {
      var store = loadStore();
      (files || []).forEach(function (f) {
        if (!f || !f.id) return;
        var tool = f.tool_slug || f.tool || f.tool_id || 'bid-drafter';
        var meta = TOOL_META[tool] || { type: 'bid', label: tool };
        var pid = 'art_' + f.id;
        if (store.items.some(function (i) { return i.id === pid || i.artifact_id === f.id; })) return;
        store.items.push({
          id: pid,
          artifact_id: f.id,
          tool: tool,
          type: meta.type,
          title: f.title || 'Saved work',
          status: tool === 'rfp-decoder' ? 'open' : 'pending',
          created_at: f.created_at || new Date().toISOString(),
          updated_at: f.updated_at || f.created_at || new Date().toISOString(),
          margin_checked: tool === 'margin-sentinel',
          output_preview: (f.body_markdown || f.content || f.preview || '').slice(0, 500)
        });
      });
      saveStore(store, { skipSync: true });
      return store.items;
    }).catch(function () { return null; });
  }

  function pullPipelineFromAccount() {
    if (!global.OporaEdge || !global.OporaEdge.isAuthed || !global.OporaEdge.isAuthed()) {
      return mergeArtifactsIntoPipeline();
    }
    return global.OporaEdge.fetchApi('/api/edge/pipeline/list').then(function (res) {
      return res.json();
    }).then(function (data) {
      if (!data || data.ok === false) return mergeArtifactsIntoPipeline();
      var stages = (data.data && data.data.stages) || {};
      var flat = [];
      Object.keys(stages).forEach(function (k) { flat = flat.concat(stages[k] || []); });
      if (!flat.length) flat = data.items || data.deals || [];
      var store = loadStore();
      flat.forEach(function (deal) {
        if (!deal || !deal.id) return;
        var mapped = mapDealToItem(deal);
        var idx = store.items.findIndex(function (i) { return i.id === deal.id; });
        if (idx >= 0) store.items[idx] = Object.assign({}, store.items[idx], mapped);
        else store.items.push(mapped);
      });
      saveStore(store, { skipSync: true });
      return mergeArtifactsIntoPipeline();
    }).catch(function () { return mergeArtifactsIntoPipeline(); });
  }

  function getItems() {
    return loadStore().items;
  }

  function getItem(id) {
    return getItems().filter(function (i) { return i.id === id; })[0] || null;
  }

  function openItems() {
    return getItems().filter(function (i) {
      return i.status === 'pending' || i.status === 'at_risk' || i.status === 'open';
    });
  }

  function inferTitle(tool, inputs, outputPreview) {
    if (inputs) {
      if (inputs.client_name) return String(inputs.client_name).slice(0, 80);
      if (inputs.account_name) return String(inputs.account_name).slice(0, 80);
      if (inputs.company_name) return String(inputs.company_name).slice(0, 80);
      if (inputs.job_name) return String(inputs.job_name).slice(0, 80);
    }
    var meta = TOOL_META[tool];
    var label = meta ? meta.label : tool;
    return label + ' — ' + new Date().toLocaleDateString();
  }

  function syncProfileFromInputs(inputs) {
    if (!inputs || !global.OporaProfile || typeof global.OporaProfile.update !== 'function') return;
    var patch = {};
    PROFILE_FIELDS.forEach(function (k) {
      if (inputs[k]) patch[k] = inputs[k];
    });
    if (inputs.state_code && !patch.state) patch.state = inputs.state_code;
    if (inputs.facility_type && !patch.vertical) patch.vertical = inputs.facility_type;
    if (Object.keys(patch).length) global.OporaProfile.update(patch);
  }

  function recordToolComplete(tool, opts) {
    opts = opts || {};
    if (!tool || !TOOL_META[tool] && tool !== 'ask-opora') return null;

    var inputs = opts.inputs || {};
    var data = opts.data || {};
    var preview = (opts.outputPreview || '').slice(0, 500);
    var meta = TOOL_META[tool] || { type: 'other', label: tool };

    syncProfileFromInputs(inputs);

    var item = {
      id: uid(),
      tool: tool,
      type: meta.type,
      title: opts.title || inferTitle(tool, inputs, preview),
      status: 'pending',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      inputs: inputs,
      output_preview: preview,
      output_data: data,
      parent_id: opts.parentId || null,
      margin_checked: tool === 'margin-sentinel',
      suggestions: []
    };

    if (tool === 'rfp-decoder') item.status = 'open';
    if (tool === 'customer-recovery') item.status = 'at_risk';

    var store = loadStore();
    store.items.unshift(item);
    saveStore(store);

    global.dispatchEvent(new CustomEvent('opora:tool-complete', {
      detail: { tool: tool, item: item }
    }));

    return item;
  }

  function setOutcome(itemId, outcome, opts) {
    opts = opts || {};
    var store = loadStore();
    var item = null;
    store.items.forEach(function (i) {
      if (i.id === itemId) item = i;
    });
    if (!item) return null;

    item.status = outcome;
    item.updated_at = new Date().toISOString();
    if (opts.note) item.outcome_note = opts.note;
    saveStore(store);

    document.querySelectorAll('.opora-thread-rail[data-pipeline-id="' + itemId + '"]').forEach(function (rail) {
      paintBidThreadRail(rail, item);
    });

    if (outcome === 'lost' && item.type === 'bid') {
      triggerThread('bid-lost', item, opts);
    }
    if (outcome === 'at_risk' && item.type === 'bid') {
      triggerThread('bid-at-risk', item, opts);
    }

    return item;
  }

  function linkChild(parentId, childId) {
    var store = loadStore();
    store.items.forEach(function (i) {
      if (i.id === childId) i.parent_id = parentId;
    });
    saveStore(store);
  }

  function triggerThread(event, item, opts) {
    opts = opts || {};
    var wire = global.OporaWire;
    if (!wire || typeof wire.handoffTo !== 'function') return;

    if (event === 'bid-lost') {
      var payload = {
        prefill_text: item.output_preview || '',
        bid_text: item.output_preview || '',
        client_name: (item.inputs && item.inputs.client_name) || item.title,
        lost_context: 'Marked lost from Operator Thread'
      };
      if (opts.autoNavigate) {
        wire.handoffTo('lost-bid-autopsy', payload, {
          source_tool: 'bid-drafter',
          wire_id: 'T1',
          source_artifact_id: item.id
        });
      } else {
        showThreadToast('Bid marked lost', 'Run Lost Bid Autopsy with this context?', function () {
          wire.handoffTo('lost-bid-autopsy', payload, {
            source_tool: 'bid-drafter',
            wire_id: 'T1',
            source_artifact_id: item.id
          });
        });
      }
    }

    if (event === 'bid-at-risk') {
      var recPayload = {
        account_name: item.title,
        situation: 'Account flagged at-risk after margin review',
        prefill_text: item.output_preview || ''
      };
      if (opts.autoNavigate) {
        wire.handoffTo('customer-recovery', recPayload, {
          source_tool: 'margin-sentinel',
          wire_id: 'T2',
          source_artifact_id: item.id
        });
      }
    }
  }

  function showThreadToast(title, body, onConfirm) {
    var el = document.createElement('div');
    el.className = 'opora-thread-toast';
    el.setAttribute('role', 'status');
    el.innerHTML =
      '<p class="opora-thread-toast__title">' + esc(title) + '</p>' +
      '<p class="opora-thread-toast__body">' + esc(body) + '</p>' +
      '<div class="opora-thread-toast__actions">' +
        '<button type="button" class="opora-thread-toast__go">Continue →</button>' +
        '<button type="button" class="opora-thread-toast__dismiss">Not now</button>' +
      '</div>';
    document.body.appendChild(el);
    requestAnimationFrame(function () { el.classList.add('is-visible'); });
    el.querySelector('.opora-thread-toast__go').addEventListener('click', function () {
      el.remove();
      if (onConfirm) onConfirm();
    });
    el.querySelector('.opora-thread-toast__dismiss').addEventListener('click', function () {
      el.classList.remove('is-visible');
      setTimeout(function () { el.remove(); }, 200);
    });
    setTimeout(function () {
      if (el.parentNode) {
        el.classList.remove('is-visible');
        setTimeout(function () { if (el.parentNode) el.remove(); }, 300);
      }
    }, 12000);
  }

  function getThreadContext() {
    var items = getItems().slice(0, 8);
    var profile = global.OporaProfile && typeof global.OporaProfile.get === 'function'
      ? global.OporaProfile.get() : {};
    return {
      profile: profile,
      recent_items: items.map(function (i) {
        return {
          id: i.id,
          tool: i.tool,
          type: i.type,
          title: i.title,
          status: i.status,
          preview: (i.output_preview || '').slice(0, 300),
          margin_checked: !!i.margin_checked
        };
      }),
      open_count: openItems().length,
      pending_bids: items.filter(function (i) {
        return i.type === 'bid' && i.status === 'pending';
      })
    };
  }

  function getSuggestions() {
    var items = getItems();
    var out = [];

    items.forEach(function (i) {
      if (i.type === 'bid' && i.status === 'pending' && !i.margin_checked) {
        out.push({
          priority: 'high',
          kind: 'margin',
          item_id: i.id,
          title: i.title,
          message: 'Check margin before you send this bid.',
          action: { type: 'handoff', tool: 'margin-sentinel', wire_id: 'W9' }
        });
      }
      if (i.type === 'rfp' && i.status === 'open') {
        out.push({
          priority: 'medium',
          kind: 'bid',
          item_id: i.id,
          title: i.title,
          message: 'RFP decoded — draft a bid response.',
          action: { type: 'handoff', tool: 'bid-drafter', wire_id: 'W3' }
        });
      }
      if (i.type === 'bid' && i.status === 'lost' && !hasChildAutopsy(i.id)) {
        out.push({
          priority: 'medium',
          kind: 'autopsy',
          item_id: i.id,
          title: i.title,
          message: 'Learn why this bid lost.',
          action: { type: 'handoff', tool: 'lost-bid-autopsy', wire_id: 'T1' }
        });
      }
    });

    return out.slice(0, 6);
  }

  function hasChildAutopsy(parentId) {
    return getItems().some(function (i) {
      return i.parent_id === parentId && i.type === 'autopsy';
    });
  }

  function collectInputs() {
    var inputs = {};
    document.querySelectorAll('.d4-f-input, .d4-f-select, textarea.d4-f-input, [name]').forEach(function (el) {
      var n = el.name || el.id;
      if (!n || n.indexOf('opora') === 0) return;
      if (el.type === 'checkbox') inputs[n] = el.checked;
      else if (el.value) inputs[n] = el.value;
    });
    return inputs;
  }

  function onToolOutput(tool, opts) {
    opts = opts || {};
    var container = opts.container;
    var preview = opts.text || '';
    if (!preview) {
      var el = container || document.querySelector('#opDocBody, #opResult');
      if (el) preview = (el.innerText || el.textContent || '').trim();
    }
    if (global.OporaTrust && typeof global.OporaTrust.filterOutput === 'function') {
      preview = global.OporaTrust.filterOutput(preview);
    }

    // Generation signal — drives coach marks / usage counters. This does NOT
    // create a pipeline deal (those listeners only need the event to fire).
    try {
      global.dispatchEvent(new CustomEvent('opora:tool-complete', { detail: { tool: tool } }));
    } catch (e) {}

    // GATE-ON-SAVE: simply generating a result no longer auto-adds it to the
    // pipeline or shows the "tracked in your pipeline" rail. Clicking Save is
    // what promotes work into the pipeline — handled by the opora:file-saved
    // listener in init() → promoteSavedFileToPipeline().

    // Margin Sentinel still reconciles against ALREADY-SAVED bids (mark checked /
    // flag at-risk). This only ever touches real, saved pipeline items.
    if (tool === 'margin-sentinel') {
      injectThreadRail('margin-sentinel', {
        id: 'ms_scan', tool: 'margin-sentinel', type: 'margin',
        status: 'pending', output_preview: preview
      }, container || document.body);
      return null;
    }

    // If this container is already tied to a SAVED pipeline item (e.g. the user
    // saved it, or re-ran a prefilled saved artifact), keep it fresh + show rail.
    var existingId = container && container.getAttribute('data-pipeline-id');
    if (existingId) {
      var item = getItem(existingId);
      if (item) {
        item.output_preview = preview.slice(0, 500);
        item.updated_at = new Date().toISOString();
        if (opts.data) item.output_data = opts.data;
        var store = loadStore();
        store.items.forEach(function (i, idx) {
          if (i.id === item.id) store.items[idx] = item;
        });
        saveStore(store, { skipSync: true });
        injectThreadRail(tool, item, container);
        return item;
      }
    }
    return null;
  }

  function currentToolSlug() {
    var meta = document.querySelector('meta[name="opora:tool-slug"]');
    if (meta && meta.content) return meta.content;
    return (global.location.pathname.match(/\/pages\/([^/?#]+)/) || [])[1] || '';
  }

  /* Promote a just-SAVED result into the pipeline + show the (now truthful)
     "tracked in your pipeline" rail on the current tool's result. */
  function promoteSavedFileToPipeline(file) {
    var tool = (file && (file.tool_slug || file.tool || file.tool_id)) || currentToolSlug();
    if (!tool) return;

    var item = null;
    if (file && file.id) {
      item = getItem('art_' + file.id) ||
        getItems().filter(function (i) { return i.artifact_id === file.id; })[0] || null;
    }
    if (!item) {
      item = getItems().filter(function (i) { return i.tool === tool; })[0] || null;
    }
    if (!item && file && file.id) {
      var meta = TOOL_META[tool] || { type: 'bid', label: tool };
      item = {
        id: 'art_' + file.id,
        artifact_id: file.id,
        tool: tool,
        type: meta.type,
        title: file.title || inferTitle(tool, {}, ''),
        status: tool === 'rfp-decoder' ? 'open' : 'pending',
        created_at: file.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString(),
        margin_checked: tool === 'margin-sentinel',
        output_preview: (file.body_markdown || file.content || '').slice(0, 500)
      };
      var store = loadStore();
      if (!store.items.some(function (i) { return i.id === item.id; })) {
        store.items.unshift(item);
        saveStore(store, { skipSync: true });
      }
    }
    if (!item) return;

    // Link a saved autopsy back to its source bid, if we came from one.
    if (tool === 'lost-bid-autopsy') {
      try {
        var fromItem = new URLSearchParams(global.location.search).get('from_item');
        if (fromItem) linkChild(fromItem, item.id);
      } catch (e) {}
    }

    // Now that it's saved, push the pipeline to the account so it shows up there.
    syncPipelineToAccount(getItems());

    // Surface the rail on the result (bid-drafter is the one with a visible rail).
    var container = document.querySelector('[data-opora-wired="' + tool + '"]') ||
      document.querySelector('[data-opora-wired]') ||
      document.querySelector('#opDocBody, #opResult');
    if (container) {
      container.setAttribute('data-pipeline-id', item.id);
      injectThreadRail(tool, item, container);
    }
  }

  function outcomeStatusLabel(status) {
    if (status === 'won') return 'Marked won — synced to your pipeline';
    if (status === 'lost') return 'Marked lost — open autopsy to learn why';
    if (status === 'at_risk') return 'At risk — margin flagged, review before submit';
    return 'Saved to your pipeline — mark it Won or Lost there once you hear back';
  }

  function paintBidThreadRail(rail, item) {
    if (!rail || !item) return;
    var status = item.status || 'pending';
    var statusEl = rail.querySelector('.opora-thread-rail__status');
    if (statusEl) {
      statusEl.textContent = outcomeStatusLabel(status);
      statusEl.setAttribute('data-status', status);
    }
    var btns = rail.querySelector('.opora-thread-rail__btns');
    if (!btns) return;
    // Won/Lost is decided later from the Pipeline (where bids live), not inline
    // right after generation. Keep this a subtle, single link.
    btns.innerHTML =
      '<a class="opora-thread-rail__link" href="' + pipelineUrl() + '" ' +
      'style="display:inline-flex;align-items:center;gap:4px;font-size:13px;font-weight:600;color:#0F7A70;text-decoration:none">' +
      'View in pipeline →</a>';
  }

  function injectThreadRail(tool, item, container) {
    if (!container || !item) return;

    var existing = container.querySelector('.opora-thread-rail');
    if (existing && existing.getAttribute('data-pipeline-id') === item.id) {
      if (tool === 'bid-drafter') paintBidThreadRail(existing, item);
      return;
    }
    if (existing) existing.remove();

    var rail = document.createElement('div');
    rail.className = 'opora-thread-rail';
    rail.setAttribute('data-pipeline-id', item.id);

    if (tool === 'bid-drafter') {
      rail.innerHTML =
        '<p class="opora-thread-rail__label">Tracked in your pipeline</p>' +
        '<p class="opora-thread-rail__status" data-status="' + esc(item.status || 'pending') + '">' +
          esc(outcomeStatusLabel(item.status || 'pending')) +
        '</p>' +
        '<div class="opora-thread-rail__btns"></div>';
      paintBidThreadRail(rail, item);
    }

    if (tool === 'margin-sentinel') {
      var bidItem = findLatestPendingBid();
      if (bidItem) {
        bidItem.margin_checked = true;
        var store = loadStore();
        store.items.forEach(function (i) {
          if (i.id === bidItem.id) i.margin_checked = true;
        });
        saveStore(store);
      }
      var lower = ((item.output_preview || '') + '').toLowerCase();
      if (/under\s+benchmark|below\s+benchmark|underpriced|margin\s+risk|below\s+floor/.test(lower)) {
        var pendingBid = findLatestPendingBid();
        if (pendingBid) {
          setOutcome(pendingBid.id, 'at_risk', { autoNavigate: false });
          showThreadToast('Margin risk flagged', 'Open customer recovery for this account?', function () {
            if (global.OporaWire && global.OporaWire.handoffTo) {
              global.OporaWire.handoffTo('customer-recovery', {
                account_name: pendingBid.title,
                situation: 'Margin Sentinel flagged underpricing risk',
                prefill_text: item.output_preview || ''
              }, { source_tool: 'margin-sentinel', wire_id: 'T2', source_artifact_id: pendingBid.id });
            }
          });
        }
      }
    }

    if (rail.innerHTML) {
      container.appendChild(rail);
    }
  }

  function findLatestPendingBid() {
    var items = getItems();
    for (var i = 0; i < items.length; i++) {
      if (items[i].type === 'bid' && items[i].status === 'pending') return items[i];
    }
    return null;
  }

  function renderPipelineStrip(mount) {
    if (!mount) return;
    var open = openItems();
    var suggestions = getSuggestions();
    var profile = global.OporaProfile && global.OporaProfile.get ? global.OporaProfile.get() : {};

    if (!open.length && !suggestions.length && !profile.state) {
      mount.setAttribute('hidden', '');
      return;
    }
    mount.removeAttribute('hidden');

    var html = '<div class="op-pipeline-strip__inner">';

    if (profile.state || profile.company_name) {
      html += '<p class="op-pipeline-strip__profile">Your profile: <strong>' +
        esc([profile.state, profile.vertical, profile.company_name].filter(Boolean).join(' · ')) +
        '</strong> <a href="/pages/business-profile">edit</a></p>';
    }

    if (open.length) {
      html += '<div class="op-pipeline-strip__section"><h2 class="op-pipeline-strip__h">Your active work</h2><ul class="op-pipeline-strip__list">';
      open.slice(0, 5).forEach(function (i) {
        html += '<li class="op-pipeline-strip__item">' +
          '<span class="op-pipeline-strip__status op-pipeline-strip__status--' + esc(i.status) + '">' + esc(i.status) + '</span>' +
          '<span class="op-pipeline-strip__title">' + esc(i.title) + '</span>' +
          '<span class="op-pipeline-strip__meta">' + esc(TOOL_META[i.tool] ? TOOL_META[i.tool].label : i.tool) + '</span>' +
          '</li>';
      });
      html += '</ul></div>';
    }

    if (suggestions.length) {
      html += '<div class="op-pipeline-strip__section"><h2 class="op-pipeline-strip__h">Suggested next</h2><ul class="op-pipeline-strip__suggestions">';
      suggestions.forEach(function (s) {
        html += '<li class="op-pipeline-strip__suggestion">' +
          '<p>' + esc(s.message) + ' <span class="op-pipeline-strip__hint">' + esc(s.title) + '</span></p>' +
          '<button type="button" class="op-pipeline-strip__cta" data-suggestion-tool="' + esc(s.action.tool) + '" data-item-id="' + esc(s.item_id) + '" data-wire="' + esc(s.action.wire_id) + '">Open tool →</button>' +
          '</li>';
      });
      html += '</ul></div>';
    }

    html += '<p class="op-pipeline-strip__foot"><a href="/pages/ai#how-connect">How tools connect →</a> · <a href="/pages/bid-studio">Bid Studio</a></p>';
    html += '</div>';
    mount.innerHTML = html;

    mount.querySelectorAll('[data-suggestion-tool]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var tool = btn.getAttribute('data-suggestion-tool');
        var itemId = btn.getAttribute('data-item-id');
        var wireId = btn.getAttribute('data-wire');
        var srcItem = getItem(itemId);
        var payload = srcItem ? {
          prefill_text: srcItem.output_preview || '',
          client_name: (srcItem.inputs && srcItem.inputs.client_name) || srcItem.title
        } : {};
        if (global.OporaWire && global.OporaWire.handoffTo) {
          global.OporaWire.handoffTo(tool, payload, {
            source_tool: srcItem ? srcItem.tool : '',
            wire_id: wireId,
            source_artifact_id: itemId
          });
        } else {
          global.location.href = '/pages/' + tool;
        }
      });
    });
  }

  function initHubStrip() {
    var mount = document.getElementById('opora-pipeline-strip');
    if (mount) renderPipelineStrip(mount);
    global.addEventListener('opora:pipeline-updated', function () {
      if (mount) renderPipelineStrip(mount);
    });
  }

  function init() {
    initHubStrip();
    pullPipelineFromAccount().then(function () {
      if (global.dispatchEvent) global.dispatchEvent(new CustomEvent('opora:pipeline-updated'));
    });
    global.addEventListener('opora:account-ready', function () {
      pullPipelineFromAccount();
    });
    global.addEventListener('opora:file-saved', function (e) {
      var file = e && e.detail && e.detail.file;
      var done = mergeArtifactsIntoPipeline();
      (done && done.then ? done : Promise.resolve()).then(function () {
        try { promoteSavedFileToPipeline(file); } catch (err) {}
      });
    });
    var params = new URLSearchParams(global.location.search);
    if (params.get('thread') === 'loss-autopsy' && params.get('from_item')) {
      var parent = getItem(params.get('from_item'));
      if (parent) triggerThread('bid-lost', parent, { autoNavigate: false });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  global.OporaSpine = {
    getItems: getItems,
    getItem: getItem,
    openItems: openItems,
    recordToolComplete: recordToolComplete,
    onToolOutput: onToolOutput,
    setOutcome: setOutcome,
    getThreadContext: getThreadContext,
    getSuggestions: getSuggestions,
    renderPipelineStrip: renderPipelineStrip,
    mergeArtifactsIntoPipeline: mergeArtifactsIntoPipeline,
    linkChild: linkChild,
    syncPipelineToAccount: syncPipelineToAccount,
    pullPipelineFromAccount: pullPipelineFromAccount
  };

})(window);
